import * as Yup from "yup";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const generateValidationSchema = (formFields) => {
  const shape = {};

  if (!Array.isArray(formFields)) {
    throw new Error("formFields must be an array of field configurations.");
  }

  formFields.forEach((field, index) => {
    try {
      if (!field || !field.type || !field.fieldId) {
        throw new Error(
          `Field at index ${index} is invalid. Ensure \"type\" and \"fieldId\" are defined.`
        );
      }

      let validator;
      console.debug(
        `Processing field: ${field.fieldName} (type: ${field.type})`
      );

      switch (field.type) {
        case "button":
          return;
        case "text":
        case "textarea":
          validator = Yup.string().trim();

          // Handle inputType validation
          if (field.validation?.inputType === "email") {
            validator = validator.email(
              `${field.fieldName} must be a valid email`
            );
          } else if (field.validation?.inputType === "number") {
            validator = Yup.string()
              .matches(/^\d+$/, `${field.fieldName} must contain only numbers`)
              .test(
                "no-e",
                `${field.fieldName} must not contain 'e'`,
                (value) => !value || !/[eE]/.test(value)
              );
          }

          if (field.required)
            validator = validator.required(`${field.fieldName} is required`);

          if (field.validation?.minLength)
            validator = validator.min(
              field.validation.minLength,
              `${field.fieldName} must be at least ${field.validation.minLength} numbers`
            );

          if (field.validation?.maxLength)
            validator = validator.max(
              field.validation.maxLength,
              `${field.fieldName} must be no more than ${field.validation.maxLength} numbers`
            );

          break;

        case "number":
          validator = Yup.number();
          if (field.required)
            validator = validator.required(`${field.fieldName} is required`);
          if (field.min !== undefined)
            validator = validator.min(
              field.min,
              `${field.fieldName} must be at least ${field.min}`
            );
          if (field.max !== undefined)
            validator = validator.max(
              field.max,
              `${field.fieldName} must be no more than ${field.max}`
            );
          break;
        case "email":
          validator = Yup.string()
            .email(`${field.fieldName} must be a valid email`)
            .trim();
          if (field.required)
            validator = validator.required(`${field.fieldName} is required`);
          break;
        case "dropdown":
          validator = field.multiple
            ? Yup.array().of(Yup.string())
            : Yup.string();
          if (field.required) {
            validator = field.multiple
              ? validator.min(1, `${field.fieldName} is required`)
              : validator.required(`${field.fieldName} is required`);
          }
          break;
        case "checkbox":
          validator = field.multiple
            ? Yup.array().min(1, `${field.fieldName} is required`)
            : Yup.boolean().oneOf([true], `${field.fieldName} is required`);
          break;
        case "radio":
          const options =
            field.optionsReference?.options || field.optionsDefault?.options;
          if (!options || !Array.isArray(options))
            throw new Error(
              `Field \"${field.fieldName}\" is missing valid options.`
            );
          validator = Yup.string()
            .required(`${field.fieldName} is required`)
            .oneOf(options.map((option) => option.value));
          break;
        case "file":
          validator = Yup.mixed()
            .nullable()
            .test(
              "fileType",
              "Invalid file type",
              (file) => !file || field.validation?.fileType?.includes(file.type)
            )
            .test(
              "fileSize",
              "File size is too large",
              (file) =>
                !file ||
                file.size <= (field.validation?.maxFileSize || Infinity) * 1024
            );
          if (field.multiple)
            validator = Yup.array()
              .of(validator)
              .min(1, `${field.fieldName} is required`)
              .max(field.validation?.maxFiles || Infinity);
          else if (field.required)
            validator = validator.required(`${field.fieldName} is required`);
          break;
        case "colorpicker":
          validator = Yup.string().matches(
            /^#[0-9A-Fa-f]{6}$/,
            `${field.fieldName} must be a valid hex color`
          );
          if (field.required)
            validator = validator.required(`${field.fieldName} is required`);
          break;
        case "date":
          validator = Yup.date()
            .nullable()
            .transform((value, originalValue) => {
              if (!originalValue || typeof originalValue !== "string")
                return null;
              const parsedDate = dayjs(
                originalValue,
                [
                  "YYYY-MM-DD",
                  "MM/DD/YYYY",
                  "DD-MM-YYYY",
                  "YYYY/MM/DD",
                  "DD/MM/YYYY",
                ],
                true
              );
              return parsedDate.isValid() ? parsedDate.toDate() : null;
            });
          if (field.required)
            validator = validator.required(`${field.fieldName} is required`);
          if (field.attribute?.maxDate)
            validator = validator.max(
              dayjs(field.attribute.maxDate).toDate(),
              `${field.fieldName} must be before ${field.attribute.maxDate}`
            );
          if (field.attribute?.minDate)
            validator = validator.min(
              dayjs(field.attribute.minDate).toDate(),
              `${field.fieldName} must be after ${field.attribute.minDate}`
            );
          break;
        case "time":
          const timeFormat =
            field.attribute?.timeFormat === "12" ? "hh:mm a" : "HH:mm";
          validator = Yup.string()
            .required(`${field.fieldName} is required`)
            .test("isValidTime", "Invalid time format", (value) =>
              dayjs(value, timeFormat, true).isValid()
            );
          if (field.attribute?.minTime)
            validator = validator.test(
              "min-time",
              `${field.fieldName} must be at or after ${field.attribute.minTime}`,
              (value) =>
                dayjs(value, timeFormat, true).isSameOrAfter(
                  dayjs(field.attribute.minTime, timeFormat, true)
                )
            );
          if (field.attribute?.maxTime)
            validator = validator.test(
              "max-time",
              `${field.fieldName} must be at or before ${field.attribute.maxTime}`,
              (value) =>
                dayjs(value, timeFormat, true).isSameOrBefore(
                  dayjs(field.attribute.maxTime, timeFormat, true)
                )
            );
          break;
        default:
          validator = Yup.string();
      }
      shape[field.fieldId] = validator;
    } catch (error) {
      console.error(
        `Error processing field \"${
          field?.fieldName || `at index ${index}`
        }\":`,
        error
      );
    }
  });

  return Yup.object().shape(shape);
};

export default generateValidationSchema;
